import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, PlusCircle } from "lucide-react";
import { Btn } from "@/components/Btn";
import { MoodVortex } from "@/components/MoodVortex";
import { CopingSelector } from "@/components/CopingSelector";
import { NeedIdeasList } from "@/components/NeedIdeas";
import { ListenButton } from "@/components/AiNote";
import { fourFConfig, moodLevel, schemaMeaning, modeMeaning } from "@/lib/config";
import { needLabel } from "@/lib/schemaFlip";
import { SECTIONS, sectionStatus, withCompletion } from "@/lib/sections";
import { useUpdateSession } from "@/lib/store";
import { sudsVar } from "@/lib/theme";

const moodTxt = (v, note) => <>{moodLevel(v)?.label} ({v}){note ? <p className="text-sm text-t-muted italic">“{note}”</p> : null}</>;

function Value({ s, k, linkedFlip }) {
  const cfg = s.survivalResponse ? fourFConfig(s.survivalResponse) : null;
  switch (k) {
    case "postSuds": return <span className="font-bold text-xl" style={{ color: sudsVar(s.postSuds) }}>{s.postSuds}</span>;
    case "triggered": return s.triggered ? `Yes${s.triggeredAt ? ` · ${new Date(s.triggeredAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}` : "No";
    case "moodBefore": return moodTxt(s.moodBefore, s.moodBeforeNote);
    case "moodNow": return moodTxt(s.moodNow, s.moodNote);
    case "copingMode": return <><b>{s.copingMode}</b>{cfg ? <span className="ml-2 font-semibold" style={{ color: `var(--${cfg.key})` }}>● {cfg.label}</span> : null}<p className="text-sm text-t-muted italic">{modeMeaning(s.copingMode)}</p></>;
    case "schemaState": return <><b>{s.schemaState}</b><p className="text-sm text-t-muted italic">{schemaMeaning(s.schemaState)}</p></>;
    case "needs": return <><p>{s.needs.map(needLabel).join(" · ")}</p>{!linkedFlip ? <div className="mt-2"><NeedIdeasList ideas={s.needIdeas} /></div> : null}</>;
    case "emaSchemas": return s.emaSchemas.join(", ");
    case "aiInsight": return <><div className="mb-2"><ListenButton text={s.aiInsight} testId="entry-insight-listen" /></div><p className="whitespace-pre-line">{s.aiInsight}</p></>;
    case "coping": return s.copingMechanisms.join(", ");
    default: return <p className="whitespace-pre-line">{s[k]}</p>;
  }
}

const initDraft = (s, sec) => ({
  postSuds: s.postSuds, triggerNote: s.triggerNote ?? "", reflectionNotes: s.reflectionNotes ?? "", copingMechanisms: s.copingMechanisms ?? [],
  mood: sec.key === "moodBefore" ? s.moodBefore : s.moodNow, note: (sec.key === "moodBefore" ? s.moodBeforeNote : s.moodNote) ?? "",
});

function Editor({ s, sec, onDone }) {
  const update = useUpdateSession();
  const [d, setD] = useState(() => initDraft(s, sec));
  const save = async (patch) => {
    await update.mutateAsync({ id: s.id, patch: withCompletion(s, patch) });
    toast.success(`${sec.label} saved`);
    onDone();
  };
  const fromDraft = () => ({
    suds: () => ({ postSuds: d.postSuds }),
    text: () => ({ [sec.key]: d[sec.key].trim() || null }),
    coping: () => ({ copingMechanisms: d.copingMechanisms }),
    mood: () => (sec.key === "moodBefore" ? { moodBefore: d.mood, moodBeforeNote: d.note.trim() || null } : { moodNow: d.mood, moodNote: d.note.trim() || null }),
  }[sec.kind])();
  if (sec.kind === "yesno") {
    return (
      <div className="flex gap-3 mt-2">
        <Btn size="md" className="flex-1" label="Yes" onClick={() => save({ triggered: true, triggeredAt: s.timestamp })} testId={`section-${sec.key}-yes`} />
        <Btn size="md" className="flex-1" variant="secondary" label="No" onClick={() => save({ triggered: false, triggeredAt: null })} testId={`section-${sec.key}-no`} />
        <Btn size="md" variant="ghost" label="Cancel" onClick={onDone} testId={`section-${sec.key}-cancel`} />
      </div>
    );
  }
  return (
    <div className="mt-2" data-testid={`section-${sec.key}-editor`}>
      {sec.kind === "suds" ? (
        <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
          {Array.from({ length: 11 }).map((_, n) => (
            <button key={n} onClick={() => setD({ ...d, postSuds: n })} data-testid={`section-suds-${n}`} className="h-11 rounded-xl border font-bold transition-colors"
              style={d.postSuds === n ? { background: sudsVar(n), borderColor: sudsVar(n), color: "#fff" } : { background: "var(--surface2)", borderColor: "var(--border)", color: "var(--on2)" }}>{n}</button>
          ))}
        </div>
      ) : null}
      {sec.kind === "text" ? <textarea className="tf-input" value={d[sec.key]} onChange={(e) => setD({ ...d, [sec.key]: e.target.value })} data-testid={`section-${sec.key}-input`} /> : null}
      {sec.kind === "coping" ? <CopingSelector value={d.copingMechanisms} onChange={(v) => setD({ ...d, copingMechanisms: v })} /> : null}
      {sec.kind === "mood" ? (
        <>
          <MoodVortex value={d.mood} onChange={(v) => setD({ ...d, mood: v })} />
          <input className="tf-input mt-3" value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} placeholder="A note (optional)" data-testid={`section-${sec.key}-note`} />
        </>
      ) : null}
      <div className="flex gap-3 mt-3">
        <Btn size="md" label="Save" loading={update.isPending} disabled={(sec.kind === "suds" && d.postSuds == null) || (sec.kind === "mood" && d.mood == null)} onClick={() => save(fromDraft())} testId={`section-${sec.key}-save`} />
        <Btn size="md" variant="ghost" label="Cancel" onClick={onDone} testId={`section-${sec.key}-cancel`} />
      </div>
    </div>
  );
}

const EMPTY = { missing: "Not filled in — tap to add", skipped: "Skipped — tap to add", optional: "Optional — tap to add", na: "Not applicable — you weren't triggered" };

function SectionRow({ s, sec, linkedFlip }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const st = sectionStatus(s, sec);
  const start = () => (sec.guided ? navigate(`/reflection/${s.id}?only=${sec.guided}`) : setOpen(true));
  return (
    <div className="py-4 border-b border-t-border last:border-0" data-testid={`section-${sec.key}`} data-status={st}>
      <div className="flex items-center justify-between gap-3">
        <p className="tf-tag">{sec.label}</p>
        {st === "done" && !open && !sec.guided ? <button onClick={start} className="text-t-muted hover:text-t-brand transition-colors" aria-label={`Edit ${sec.label}`} data-testid={`section-${sec.key}-edit`}><Pencil size={15} /></button> : null}
      </div>
      {open ? <Editor s={s} sec={sec} onDone={() => setOpen(false)} /> : st === "done" ? <div className="mt-1.5 text-t-on"><Value s={s} k={sec.key} linkedFlip={linkedFlip} /></div> : st === "na" ? (
        <p className="mt-1.5 text-sm text-t-muted">{sec.na ?? EMPTY.na}</p>
      ) : (
        <button onClick={start} data-testid={`section-${sec.key}-add`}
          className={`mt-2 w-full flex items-center gap-2 rounded-2xl border border-dashed px-4 py-3 text-sm font-semibold text-left transition-colors ${st === "missing" ? "border-t-brand text-t-brand bg-t-brand3 hover:opacity-90" : "border-t-border2 text-t-muted hover:text-t-brand hover:border-t-brand"}`}>
          <PlusCircle size={16} /> {EMPTY[st]}
        </button>
      )}
    </div>
  );
}

export function EntrySections({ s, linkedFlip }) {
  return (
    <div className="tf-card px-5" data-testid="entry-sections">
      {s.preSuds != null ? (
        <div className="py-4 border-b border-t-border">
          <p className="tf-tag">Distress at check-in</p>
          <span className="mt-1.5 inline-block font-bold text-xl" style={{ color: sudsVar(s.preSuds) }}>{s.preSuds}</span>
        </div>
      ) : null}
      {SECTIONS.map((sec) => <SectionRow key={sec.key} s={s} sec={sec} linkedFlip={linkedFlip} />)}
      {s.emergencySeconds ? <div className="py-4"><p className="tf-tag">Time in override</p><p className="mt-1.5 text-t-on">{Math.round(s.emergencySeconds / 6) / 10} min</p></div> : null}
    </div>
  );
}
